import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import {
  requireAdmin,
  requireAuditAccess,
  requireAuth,
  requireComplianceManager,
} from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { AuditDetailPage } from "./routes/dashboard/audits/detail";
import { AuditsPage } from "./routes/dashboard/audits/index";
import { DashboardPage } from "./routes/dashboard/index";
import { ItemsPage } from "./routes/dashboard/items";
import { ItemDetailPage } from "./routes/dashboard/items/detail";
import { ProjectDetailPage } from "./routes/dashboard/projects/detail";
import { ProjectsPage } from "./routes/dashboard/projects/index";
import { ReportsPage } from "./routes/dashboard/reports";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsTechPartnersPage } from "./routes/dashboard/settings/tech-partners";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { TemplateDetailPage } from "./routes/dashboard/templates/detail";
import { TemplatesPage } from "./routes/dashboard/templates/index";
import { NewTemplatePage } from "./routes/dashboard/templates/new";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  component: DashboardPage,
});

const itemsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items",
  component: ItemsPage,
});

const itemDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items/$itemId",
  component: ItemDetailPage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin(ctx);
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsTechPartnersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/tech-partners",
  component: SettingsTechPartnersPage,
});

const settingsAuditStatusesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/audit-statuses",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/settings/system" });
  },
});

const templatesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/templates",
  beforeLoad: requireComplianceManager,
  component: TemplatesPage,
});

const newTemplateRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/templates/new",
  beforeLoad: requireComplianceManager,
  component: NewTemplatePage,
});

const templateDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/templates/$templateId",
  beforeLoad: requireComplianceManager,
  component: TemplateDetailPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/projects",
  component: ProjectsPage,
});

const projectDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/projects/$projectId",
  component: ProjectDetailPage,
});

const auditsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/audits",
  beforeLoad: requireAuditAccess,
  component: AuditsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    status: typeof search.status === "string" ? search.status : undefined,
  }),
});

const auditDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/audits/$auditId",
  beforeLoad: requireAuditAccess,
  component: AuditDetailPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reports",
  beforeLoad: requireAuditAccess,
  component: ReportsPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    itemsRoute,
    itemDetailRoute,
    projectsRoute,
    projectDetailRoute,
    templatesRoute,
    newTemplateRoute,
    templateDetailRoute,
    auditsRoute,
    auditDetailRoute,
    reportsRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsSystemRoute,
      settingsTechPartnersRoute,
      settingsAuditStatusesRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
